/**
 * Relations Panel - OData $expand Builder UI
 *
 * Allows visual configuration of OData $expand for related data.
 * Loads available relations from the Entity Registry and enables:
 * - Selection of Navigation Properties
 * - $select within $expand
 * - $filter within $expand
 * - Nested $expand (nested relations)
 */

import React, { useState, useCallback, useMemo } from 'react';
import {
  Plus,
  Trash2,
  ChevronDown,
  ChevronRight,
  Link2,
  Settings,
  Check,
  Database,
  Layers,
} from 'lucide-react';
import type { ExpandRelation } from '../../types';

interface RelationsPanelProps {
  relations: ExpandRelation[];
  availableRelations: Array<{
    name: string;
    targetEntity: string;
    multiplicity: '1' | '*';
  }>;
  availableFields?: Array<{ name: string; dataType: string }>;
  onChange: (relations: ExpandRelation[]) => void;
  entityName?: string;
}

// Filter Operators (same as in ServerFilterPanel)
const FILTER_OPERATORS = [
  { value: 'eq', label: '=' },
  { value: 'ne', label: '≠' },
  { value: 'gt', label: '>' },
  { value: 'ge', label: '≥' },
  { value: 'lt', label: '<' },
  { value: 'le', label: '≤' },
  { value: 'contains', label: 'Contains' },
];

export function RelationsPanel({
  relations,
  availableRelations,
  availableFields = [],
  onChange,
  entityName,
}: RelationsPanelProps) {
  const [expandedItems, setExpandedItems] = useState<Set<string>>(new Set());
  const [configuring, setConfiguring] = useState<string | null>(null);

  // Toggle relation enabled/disabled
  const toggleRelation = useCallback((relationName: string) => {
    const existing = relations.find(r => r.entity === relationName);
    if (existing) {
      // Remove if already exists
      onChange(relations.filter(r => r.entity !== relationName));
    } else {
      // Add new relation
      const available = availableRelations.find(r => r.name === relationName);
      if (available) {
        onChange([...relations, {
          entity: available.name,
          targetEntity: available.targetEntity,
          multiplicity: available.multiplicity,
          enabled: true,
        }]);
      }
    }
  }, [relations, availableRelations, onChange]);

  // Update relation config
  const updateRelation = useCallback((relationName: string, updates: Partial<ExpandRelation>) => {
    onChange(relations.map(r =>
      r.entity === relationName ? { ...r, ...updates } : r
    ));
  }, [relations, onChange]);

  // Add filter to relation
  const addFilterToRelation = useCallback((relationName: string) => {
    const relation = relations.find(r => r.entity === relationName);
    if (relation) {
      const newFilter = { field: '', operator: 'eq', value: '' };
      updateRelation(relationName, {
        filter: [...(relation.filter || []), newFilter]
      });
    }
  }, [relations, updateRelation]);

  // Update filter in relation
  const updateFilterInRelation = useCallback((
    relationName: string,
    filterIndex: number,
    updates: Partial<{ field: string; operator: string; value: string | number | boolean }>
  ) => {
    const relation = relations.find(r => r.entity === relationName);
    if (relation?.filter) {
      const newFilters = [...relation.filter];
      newFilters[filterIndex] = { ...newFilters[filterIndex], ...updates };
      updateRelation(relationName, { filter: newFilters });
    }
  }, [relations, updateRelation]);

  // Remove filter from relation
  const removeFilterFromRelation = useCallback((relationName: string, filterIndex: number) => {
    const relation = relations.find(r => r.entity === relationName);
    if (relation?.filter) {
      const newFilters = relation.filter.filter((_, i) => i !== filterIndex);
      updateRelation(relationName, { filter: newFilters.length > 0 ? newFilters : undefined });
    }
  }, [relations, updateRelation]);

  // Toggle field selection in relation
  const toggleFieldInRelation = useCallback((relationName: string, fieldName: string) => {
    const relation = relations.find(r => r.entity === relationName);
    if (relation) {
      const currentSelect = relation.select || [];
      const newSelect = currentSelect.includes(fieldName)
        ? currentSelect.filter(f => f !== fieldName)
        : [...currentSelect, fieldName];
      updateRelation(relationName, {
        select: newSelect.length > 0 ? newSelect : undefined
      });
    }
  }, [relations, updateRelation]);

  // Toggle expanded state
  const toggleExpanded = useCallback((relationName: string) => {
    setExpandedItems(prev => {
      const newSet = new Set(prev);
      if (newSet.has(relationName)) {
        newSet.delete(relationName);
      } else {
        newSet.add(relationName);
      }
      return newSet;
    });
  }, []);

  // Get enabled relations
  const enabledRelations = useMemo(() =>
    relations.filter(r => r.enabled),
    [relations]
  );

  // Get available but not enabled relations
  const availableNotEnabled = useMemo(() =>
    availableRelations.filter(ar => !relations.some(r => r.entity === ar.name)),
    [availableRelations, relations]
  );

  if (availableRelations.length === 0) {
    return (
      <div className="p-4 bg-gray-50 rounded-lg text-center">
        <Layers className="w-8 h-8 mx-auto text-gray-400 mb-2" />
        <p className="text-sm text-gray-500">
          No relations available for this entity.
        </p>
        <p className="text-xs text-gray-400 mt-1">
          Relations are loaded from OData $metadata.
        </p>
      </div>
    );
  }

  return (
    <div className="space-y-4">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-2">
          <Link2 className="w-5 h-5 text-gray-500" />
          <h3 className="font-medium text-gray-900">Related Data ($expand)</h3>
        </div>
        <span className="text-xs text-gray-500">
          {enabledRelations.length} of {availableRelations.length} active
        </span>
      </div>

      {/* Info */}
      <p className="text-sm text-gray-500">
        Load related data from other LN tables in a single query.
      </p>

      {/* Relations List */}
      <div className="space-y-2">
        {availableRelations.map((availableRel) => {
          const relation = relations.find(r => r.entity === availableRel.name);
          const isEnabled = !!relation;
          const isExpanded = expandedItems.has(availableRel.name);

          return (
            <div
              key={availableRel.name}
              className={`border rounded-lg transition-colors ${
                isEnabled ? 'border-blue-300 bg-blue-50' : 'border-gray-200 bg-white'
              }`}
            >
              {/* Relation Header */}
              <div className="flex items-center gap-3 p-3">
                {/* Checkbox */}
                <button
                  onClick={() => toggleRelation(availableRel.name)}
                  className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                    isEnabled
                      ? 'bg-blue-500 border-blue-500 text-white'
                      : 'border-gray-300 hover:border-blue-400'
                  }`}
                >
                  {isEnabled && <Check className="w-3 h-3" />}
                </button>

                {/* Expand/Collapse */}
                {isEnabled && (
                  <button
                    onClick={() => toggleExpanded(availableRel.name)}
                    className="p-1 text-gray-400 hover:text-gray-600"
                  >
                    {isExpanded ? (
                      <ChevronDown className="w-4 h-4" />
                    ) : (
                      <ChevronRight className="w-4 h-4" />
                    )}
                  </button>
                )}

                {/* Relation Info */}
                <div className="flex-1">
                  <div className="flex items-center gap-2">
                    <span className={`font-medium ${isEnabled ? 'text-blue-700' : 'text-gray-700'}`}>
                      {availableRel.name}
                    </span>
                    <span className={`text-xs px-1.5 py-0.5 rounded ${
                      availableRel.multiplicity === '*'
                        ? 'bg-purple-100 text-purple-700'
                        : 'bg-green-100 text-green-700'
                    }`}>
                      {availableRel.multiplicity === '*' ? 'Viele' : 'Eins'}
                    </span>
                  </div>
                  <p className="text-xs text-gray-500">
                    → {availableRel.targetEntity}
                  </p>
                </div>

                {/* Config Badge */}
                {isEnabled && relation && (relation.select?.length || relation.filter?.length) && (
                  <div className="flex items-center gap-1 text-xs text-blue-600">
                    <Settings className="w-3 h-3" />
                    <span>
                      {relation.select?.length ? `${relation.select.length} fields` : ''}
                      {relation.select?.length && relation.filter?.length ? ', ' : ''}
                      {relation.filter?.length ? `${relation.filter.length} Filter` : ''}
                    </span>
                  </div>
                )}
              </div>

              {/* Expanded Configuration */}
              {isEnabled && isExpanded && relation && (
                <div className="border-t border-blue-200 p-3 space-y-4 bg-white rounded-b-lg">
                  {/* Field Selection */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Select Fields ($select)
                      </label>
                      <span className="text-xs text-gray-400">
                        {relation.select?.length || 0} selected
                      </span>
                    </div>
                    <div className="flex flex-wrap gap-1 p-2 bg-gray-50 rounded border max-h-32 overflow-auto">
                      {/* Placeholder fields based on target entity - in reality would come from metadata */}
                      {['ID', 'Name', 'Description', 'Status', 'Amount', 'Quantity', 'Date', 'Code'].map((field) => (
                        <button
                          key={field}
                          onClick={() => toggleFieldInRelation(availableRel.name, field)}
                          className={`px-2 py-1 text-xs rounded transition-colors ${
                            relation.select?.includes(field)
                              ? 'bg-blue-500 text-white'
                              : 'bg-white border border-gray-200 text-gray-600 hover:border-blue-300'
                          }`}
                        >
                          {field}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      Empty = Load all fields
                    </p>
                  </div>

                  {/* Filters for Relation */}
                  <div>
                    <div className="flex items-center justify-between mb-2">
                      <label className="text-sm font-medium text-gray-700">
                        Filter ($filter)
                      </label>
                      <button
                        onClick={() => addFilterToRelation(availableRel.name)}
                        className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 hover:bg-blue-50 rounded"
                      >
                        <Plus className="w-3 h-3" />
                        Filter
                      </button>
                    </div>

                    {relation.filter && relation.filter.length > 0 ? (
                      <div className="space-y-2">
                        {relation.filter.map((filter, idx) => (
                          <div key={idx} className="flex items-center gap-2">
                            <input
                              type="text"
                              value={filter.field}
                              onChange={(e) => updateFilterInRelation(availableRel.name, idx, { field: e.target.value })}
                              placeholder="Field"
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <select
                              value={filter.operator}
                              onChange={(e) => updateFilterInRelation(availableRel.name, idx, { operator: e.target.value })}
                              className="px-2 py-1 text-sm border border-gray-300 rounded"
                            >
                              {FILTER_OPERATORS.map(op => (
                                <option key={op.value} value={op.value}>{op.label}</option>
                              ))}
                            </select>
                            <input
                              type="text"
                              value={String(filter.value)}
                              onChange={(e) => updateFilterInRelation(availableRel.name, idx, { value: e.target.value })}
                              placeholder="Value"
                              className="flex-1 px-2 py-1 text-sm border border-gray-300 rounded"
                            />
                            <button
                              onClick={() => removeFilterFromRelation(availableRel.name, idx)}
                              className="p-1 text-red-400 hover:text-red-600"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        ))}
                      </div>
                    ) : (
                      <p className="text-xs text-gray-400 italic p-2 bg-gray-50 rounded">
                        No filters - all records will be loaded
                      </p>
                    )}
                  </div>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Summary */}
      {enabledRelations.length > 0 && (
        <div className="p-3 bg-green-50 rounded-lg border border-green-200">
          <div className="flex items-center gap-2 text-green-700">
            <Database className="w-4 h-4" />
            <span className="text-sm font-medium">
              {enabledRelations.length} relation{enabledRelations.length > 1 ? 's' : ''} will be loaded
            </span>
          </div>
          <p className="text-xs text-green-600 mt-1">
            {enabledRelations.map(r => r.entity).join(', ')}
          </p>
        </div>
      )}
    </div>
  );
}

export default RelationsPanel;
